import { OverviewDetachSensorFromMeasuringPointDocument } from '@/graphql/types';
import { getClient } from './utils/apollo-client';

import { confirm } from './components/confirm/Confirm';
import copyModal, {
    type CopyModalOptions,
    MS_BEFORE_RELOAD_ON_SUCCESS,
} from './components/pages/measuringPoint/copyModal';

import {
    addNotificationHack,
    NOTIFICATION_VARIANT,
} from './components/notifications/Notifications';
import warning from './utils/logger';
import type { SwarmType } from './enums';

interface PopupConfig {
    className: string;
    data: string[];
    url: string;
    urlKey: string;
}

const popupList: PopupConfig[] = [
    // Delete Measuring Point.
    {
        className: 'delete-mp',
        data: ['mp'],
        url: '/measuring_point/delete/',
        urlKey: 'mp',
    },
    // Delete Project.
    {
        className: 'delete-project',
        data: ['project'],
        url: '/project/delete/',
        urlKey: 'project',
    },
    // Archive Project.
    {
        className: 'archive-project',
        data: ['project'],
        url: '/project/archive/',
        urlKey: 'project',
    },
    // Archive Measuring Point.
    {
        className: 'archive-mp',
        data: ['mp'],
        url: '/measuring_point/archive/',
        urlKey: 'mp',
    },
    // Detach Measuring Point.
    {
        className: 'detach-mp',
        data: ['mp'],
        url: '/measuring_point/unmount/',
        urlKey: 'mp',
    },
    // Undo Sharing Measuring Point.
    {
        className: 'unshare-mp',
        data: ['mp'],
        url: '/sharing/share-measuring-point-delete/',
        urlKey: 'mp',
    },
    // Undo Sharing Project.
    {
        className: 'unshare-project',
        data: ['project'],
        url: '/sharing/share-project-delete/',
        urlKey: 'project',
    },
    // Restart Measuring Point.
    {
        className: 'restart-mp',
        data: ['mp'],
        url: '/measuring_point/restart/',
        urlKey: 'mp',
    },
];

async function detachSensorFromMeasuringPoint(id: string): Promise<void> {
    const mutationParameters = {
        variables: { id },
        mutation: OverviewDetachSensorFromMeasuringPointDocument,
    };

    await getClient()
        .mutate(mutationParameters)
        .then((_result) => {
            addNotificationHack({
                message: gettext('SENSOR_DETACHED_SUCCESSFULLY'),
                variant: NOTIFICATION_VARIANT.SUCCESS,
                scrollToTop: true,
            });
            setTimeout(() => window.location.reload(), MS_BEFORE_RELOAD_ON_SUCCESS);
        })
        .catch((error: Error) => {
            addNotificationHack({
                message: gettext('SENSOR_DETACH_FAILED'),
                variant: NOTIFICATION_VARIANT.ERROR,
                scrollToTop: true,
            });
            warning({ message: error });
        });
}

interface CopySettingDataset extends DOMStringMap {
    id: string;
    swarmType: SwarmType;
    source: CopyModalOptions['source'];
    destination: CopyModalOptions['destination'];
}

interface PopupDataset extends DOMStringMap {
    message: string;
    mp?: string;
    project?: string;
}

function prefersClickToggle() {
    if (typeof window === 'undefined') {
        return false;
    }

    if (typeof window.matchMedia !== 'function') {
        return false;
    }

    return (
        window.matchMedia('(hover: none)').matches || window.matchMedia('(pointer: coarse)').matches
    );
}

function setupClickToggle({
    selector,
    openClass,
    ignoreSelector,
}: {
    selector: string;
    openClass: string;
    ignoreSelector?: string;
}): void {
    const elements = Array.from(document.querySelectorAll(selector));

    if (!elements.length || !prefersClickToggle()) {
        return;
    }

    function setExpanded(element: Element, isOpen: boolean): void {
        if (element.hasAttribute('aria-expanded')) {
            element.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        }
    }

    function closeAll(): void {
        elements.forEach((element) => {
            element.classList.remove(openClass);
            setExpanded(element, false);
        });
    }

    elements.forEach((element) => {
        element.addEventListener('click', (event) => {
            if (ignoreSelector && (event.target as Element).closest(ignoreSelector)) {
                return;
            }

            const isOpen = element.classList.contains(openClass);
            closeAll();
            if (!isOpen) {
                element.classList.add(openClass);
                setExpanded(element, true);
            }
        });
    });

    document.addEventListener('click', (event) => {
        if (!(event.target as Element).closest(selector)) {
            closeAll();
        }
    });
}

function enableStatusCellDetailsToggle(): void {
    setupClickToggle({
        selector: '.status-cell--has-details',
        openClass: 'status-cell--is-open',
        ignoreSelector: '.status-cell__info',
    });
}

function enableBatteryTooltipToggle(): void {
    setupClickToggle({
        selector: '.device-name__info[data-battery-tooltip]',
        openClass: 'device-name__info--is-open',
    });
}

function measuringPointOverviewPage(): void {
    Array.from(document.getElementsByClassName('copy-setting')).forEach((element) => {
        const dataset = (element as HTMLElement).dataset as CopySettingDataset;

        element.addEventListener('click', (e) => {
            e.preventDefault();

            copyModal({
                id: parseInt(dataset.id, 10),
                swarmType: dataset.swarmType,
                source: dataset.source,
                destination: dataset.destination,
                // This page is rendered by Django and has no Apollo queries to refetch.
                refetchQueries: [],
                onSuccess() {
                    // As the overview page is not yet converted to React, we currently
                    // just reload the page to get the newly created measuring point or
                    // project visible.
                    // Issue about converting the overview page:
                    // https://github.com/omnidots/website/issues/5579
                    setTimeout(() => window.location.reload(), MS_BEFORE_RELOAD_ON_SUCCESS);
                },
            });
        });
    });

    // Attaches the confirmation popup to the specified button.
    function popupGenerator({ className, url, urlKey }: PopupConfig): void {
        Array.from(document.getElementsByClassName(className)).forEach((element) => {
            const dataset = (element as HTMLElement).dataset as PopupDataset;
            const { message, ...rest } = dataset;
            element.addEventListener('click', (e) => {
                e.preventDefault();
                confirm({
                    markdownMessage: message,
                    onConfirm() {
                        if (className === 'detach-mp') {
                            void detachSensorFromMeasuringPoint(rest.mp!);
                        } else {
                            window.location.href = url + rest[urlKey]!;
                        }
                    },
                });
            });
        });
    }

    popupList.forEach(({ className, data, url, urlKey }) =>
        popupGenerator({
            className,
            data,
            url,
            urlKey,
        })
    );

    enableStatusCellDetailsToggle();
    enableBatteryTooltipToggle();
}

export default measuringPointOverviewPage;
